// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { DocumentType, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogDeleteDocumentTypeProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogDeleteDocumentType = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogDeleteDocumentTypeProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDocumentType = searchParams.get('dialogDocumentType');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [documentType, setDocumentType] = useState<DocumentType | null>(null);

	// fetch document type
	const { isLoading: isLoadingDocumentType } = useFetch({
		isEnabled: Boolean(paramDocumentType && paramDialog === 'deleteDocumentType'),
		url: `${process.env.REACT_APP_API_URL}/document-types/${paramDocumentType}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocumentType(res.data[0]);
			}
		},
	});

	// delete document type
	const { fetchRequest: deleteDocumentType, isLoading: isLoadingDeleteDocumentType } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/document-types/${paramDocumentType}`,
		options: {
			method: 'DELETE',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onError: (error) => {
			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (
				['DOCUMENT_TYPE_DOCUMENTS_EXIST', 'DOCUMENT_TYPE_EXPENSE_TYPES_EXIST'].includes(error.id)
			) {
				feedbackNew.title = {
					children: t('documentTypeCannotBeDeleted'),
				};

				if (error.id === 'DOCUMENT_TYPE_DOCUMENTS_EXIST') {
					feedbackNew.alert.children = t('error.documentTypeDocumentsExist');
				}

				if (error.id === 'DOCUMENT_TYPE_EXPENSE_TYPES_EXIST') {
					feedbackNew.alert.children = t('error.documentTypeExpenseTypesExist');
				}
			}

			if (error.id === 'DOCUMENT_TYPE_UNAUTHORIZED_DELETE') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.documentTypeDeleted', { name: documentType?.name }),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// delete document type
		deleteDocumentType();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingDocumentType) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.green.700" component="span" fontWeight={600} />,
						}}
						i18nKey="dialogDeleteDocumentType.content"
						values={{ name: documentType?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.deleteDocumentType')}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('deleteDocumentType'),
					color: 'error',
					loading: isLoadingDeleteDocumentType,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'documentType',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: isLoadingDocumentType ? '' : documentType?.name,
			}}
			title={{
				children: t('deleteDocumentType'),
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
